import * as React from 'react'
import { Clock, MapPin } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { accentText } from './badge-colors'
import {
  type CalendarEvent,
  formatTime,
  isSameDay,
} from './event-calendar-utils'

/**
 * EventCard — a single event as a standalone card, led by a big date. Two looks:
 *
 *  - `filled` (default): a bold brand-gradient hero card with white text, for a
 *    featured / next-up event.
 *  - `outline`: a white, bordered card that tints the date + accents with the
 *    event's own `color` — for grids and lists of events.
 *
 *   <EventCard event={event} />
 *   <EventCard event={event} variant="outline" onClick={open} />
 *
 * Shows the day + month/year, title, time range (or "All day"), and — when set —
 * the event's `location`. The `filled` look is intentionally brand-only (not the
 * per-event color) so white text always clears AA contrast; `outline` is the one
 * that carries the event color, on a light surface where any palette color is
 * legible.
 */

export type EventCardVariant = 'filled' | 'outline'
export type EventCardSize = 'md' | 'sm'

/** Per-size metrics. */
const cardSize: Record<
  EventCardSize,
  {
    pad: string
    dateGap: string
    day: string
    monthYear: string
    title: string
    text: string
    icon: string
    detailGap: string
    radius: string
  }
> = {
  md: {
    pad: 'p-6',
    dateGap: 'mb-8',
    day: 'text-4xl',
    monthYear: 'text-sm',
    title: 'mb-4 text-xl',
    text: 'text-sm',
    icon: 'size-5',
    detailGap: 'mt-2',
    radius: 'rounded-2xl',
  },
  sm: {
    pad: 'p-4',
    dateGap: 'mb-4',
    day: 'text-3xl',
    monthYear: 'text-xs',
    title: 'mb-2 text-base',
    text: 'text-xs',
    icon: 'size-4',
    detailGap: 'mt-1.5',
    radius: 'rounded-xl',
  },
}

const monthYearFmt = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
})
/** "March, 2025" — long month, comma, year. */
const formatMonthYearComma = (d: Date): string => {
  const parts = monthYearFmt.formatToParts(d)
  const month = parts.find((p) => p.type === 'month')?.value ?? ''
  const year = parts.find((p) => p.type === 'year')?.value ?? ''
  return `${month}, ${year}`
}

/** The event's time range: "8:00 AM – 10:00 AM", a single time, or "All day". */
const timeLabel = (event: CalendarEvent): string => {
  if (event.allDay) return 'All day'
  if (event.end && !isSameDay(event.start, event.end)) {
    return `${formatTime(event.start)} – ${formatTime(event.end)} (multi-day)`
  }
  return event.end
    ? `${formatTime(event.start)} – ${formatTime(event.end)}`
    : formatTime(event.start)
}

export interface EventCardProps {
  event: CalendarEvent
  /** `filled` brand hero (default) or `outline` (event-colored, on white). */
  variant?: EventCardVariant
  /** `md` (default) or `sm` — a denser card for tighter placements. */
  size?: EventCardSize
  /** Makes the whole card a single clickable target. */
  onClick?: () => void
  className?: string
}

export const EventCard = React.forwardRef<HTMLElement, EventCardProps>(
  ({ event, variant = 'filled', size = 'md', onClick, className }, ref) => {
    const filled = variant === 'filled'
    const color = event.color ?? 'primary'
    const s = cardSize[size]

    return (
      <article
        ref={ref}
        className={cn(
          'relative w-full max-w-md',
          s.radius,
          s.pad,
          filled
            ? 'bg-gradient-to-br from-fbui-basil-600 to-fbui-basil-500 text-fbui-base-white shadow-fbui-md'
            : 'border border-fbui-gray-200 bg-fbui-base-white shadow-fbui-sm',
          className
        )}
      >
        {/* Date */}
        <div className={s.dateGap}>
          <div
            className={cn(
              'font-bold leading-none',
              s.day,
              !filled && accentText[color]
            )}
          >
            {event.start.getDate()}
          </div>
          <div
            className={cn(
              'mt-1',
              s.monthYear,
              filled ? 'opacity-90' : 'text-fbui-gray-500'
            )}
          >
            {formatMonthYearComma(event.start)}
          </div>
        </div>

        {/* Details */}
        <div>
          <h3
            className={cn(
              'font-semibold',
              s.title,
              filled ? 'text-fbui-base-white' : 'text-fbui-gray-900'
            )}
          >
            {event.title}
          </h3>

          <div
            className={cn(
              'flex items-center gap-2',
              s.text,
              filled ? 'opacity-90' : 'text-fbui-gray-600'
            )}
          >
            <Clock
              className={cn(s.icon, 'shrink-0', !filled && 'text-fbui-gray-400')}
            />
            <span>{timeLabel(event)}</span>
          </div>

          {event.location && (
            <div
              className={cn(
                'flex items-center gap-2',
                s.detailGap,
                s.text,
                filled ? 'opacity-90' : 'text-fbui-gray-600'
              )}
            >
              <MapPin
                className={cn(
                  s.icon,
                  'shrink-0',
                  !filled && 'text-fbui-gray-400'
                )}
              />
              <span>{event.location}</span>
            </div>
          )}
        </div>

        {/* Whole-card click target (kept last so it lies over the content). */}
        {onClick && (
          <button
            type="button"
            aria-label={event.title}
            onClick={onClick}
            className={cn(
              'absolute inset-0 outline-none focus-visible:ring-2',
              s.radius,
              filled
                ? 'focus-visible:ring-fbui-base-white/70'
                : 'focus-visible:ring-fbui-ring-brand'
            )}
          />
        )}
      </article>
    )
  }
)
EventCard.displayName = 'EventCard'
